import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { fadeUp, revealProps, stagger, useReducedMotion } from "homepage-new/motion";
import { rf } from "../theme";
import { Band, Card, Display } from "../ui";

// A cohort has real disadvantages, and saying them here costs us the wrong
// customers — which is the point.
const NOT = [
  {
    title: "Attention is shared",
    body: "One instructor, one syllabus, a room of people. You get attention, but you share it. If what you need is a plan built entirely around your own gaps, a batch is the wrong shape and we will tell you so.",
  },
  {
    title: "It does not wait for you",
    body: "The batch moves on Tuesday whether or not your week went well. Recordings help; they do not replace being there. Roughly eight hours a week, and it is not optional.",
  },
  {
    title: "It is not a certificate mill",
    body: "You finish with a deployed system and a repository worth reading. There is a certificate, and it is worth exactly what any certificate is worth — nothing, next to the work.",
  },
  {
    title: "We do not promise a job",
    body: "We prepare you for the interviews and tell you honestly where you stand. Anyone guaranteeing placement is pricing that promise into your fee.",
  },
];

const INCLUDED = [
  "Every live session, plus recordings kept for a year",
  "Weekly code review by the person teaching the class",
  "Capstone review and one mock system-design interview",
  "Full refund within seven days of the start date",
];

export const Honest = () => {
  const reduced = useReducedMotion();

  return (
    <Band
      id="honest"
      band
      eyebrow="Before you pay"
      title="What a cohort is bad at"
      lead="The batch format buys you a lower price and people to work alongside. It costs you flexibility. Here is that trade in plain words."
    >
      <motion.div variants={stagger(0.08)} {...revealProps(reduced)}>
        <Grid container spacing={3} sx={{ mb: { xs: 4, md: 6 } }}>
          {NOT.map((item) => (
            <Grid item xs={12} md={6} key={item.title}>
              <motion.div variants={fadeUp} style={{ height: "100%" }}>
                <Card sx={{ p: 3.25, height: "100%", textAlign: "left" }}>
                  <Display component="h3" size={22} sx={{ mb: 1 }}>
                    {item.title}
                  </Display>
                  <Typography sx={{ fontFamily: rf.body, fontSize: 15, color: rf.inkSoft, lineHeight: 1.65 }}>
                    {item.body}
                  </Typography>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <motion.div variants={fadeUp}>
          <Card bg={rf.coral} shadow={rf.shadowLg} sx={{ p: { xs: 3, md: 4.5 }, textAlign: "left", maxWidth: 880, mx: "auto" }}>
            <Display component="h3" size={28} sx={{ mb: 2.5 }}>
              What the fee covers
            </Display>
            <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 1.25 }}>
              {INCLUDED.map((line) => (
                <Box
                  component="li"
                  key={line}
                  sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", backgroundColor: rf.paper, border: rf.lineThin, borderRadius: rf.radiusSm, px: 2, py: 1.25 }}
                >
                  <Box sx={{ width: 9, height: 9, backgroundColor: rf.ink, borderRadius: "2px", mt: "7px", flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: rf.body, fontSize: 15, fontWeight: 600, color: rf.ink, lineHeight: 1.5 }}>
                    {line}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, color: rf.ink, mt: 2.5, lineHeight: 1.6, fontWeight: 600 }}>
              No separate "material fee" and no upsell mid-cohort — the number on the cohort card is the number you
              pay. Cloud compute for the heavier weeks is the one thing it does not cover, and we tell you what to
              expect before you spend anything.
            </Typography>
          </Card>
        </motion.div>
      </motion.div>
    </Band>
  );
};

export default Honest;
